import { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Stack, Group, Select, Textarea, Alert } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { CodeableConcept, Procedure } from '@medplum/fhirtypes';
import { CodeableConceptInput } from '@medplum/react';
import { isEqual } from 'lodash';
import FormActionButtons, {
  FormActionButtonsRef,
} from '@/components/FormActionButtons';
import {
  THERAPEUTIC_SERVICE_VALUESET,
  STATUS_OPTIONS,
} from '@consts/therapeutic-service.consts';
import { DateInputWrapper } from '@/components/inputs/DateInputWrapper';

export interface TherapeuticServiceFormData {
  id?: string;
  service: CodeableConcept | undefined;
  startDate: Date | null;
  terminationDate: Date | null;
  response: string;
  status?: Procedure['status'];
  encounterId?: string;
}

export interface TherapeuticServiceModalProps {
  opened: boolean;
  onClose: () => void;
  onSave: (service: TherapeuticServiceFormData) => void;
  service?: TherapeuticServiceFormData;
  mode: 'add' | 'edit';
}

// Default empty service for new entries
const createEmptyService = (): TherapeuticServiceFormData => ({
  service: undefined,
  startDate: null,
  terminationDate: null,
  response: '',
  status: 'in-progress',
  encounterId: undefined,
});

export const TherapeuticServiceModal = ({
  opened,
  onClose,
  onSave,
  service,
  mode,
}: TherapeuticServiceModalProps) => {
  const { t } = useTranslation(['patient-pages']);

  // Form state
  const [formData, setFormData] =
    useState<TherapeuticServiceFormData>(createEmptyService());
  const [validationError, setValidationError] = useState<string | null>(null);

  // Key to force CodeableConceptInput remount on each modal open
  const [inputKey, setInputKey] = useState(0);

  // Refs for FormActionButtons pattern
  const formActionsRef = useRef<FormActionButtonsRef>(null);
  const baselineRef = useRef<TherapeuticServiceFormData | null>(null);
  const [baselineSet, setBaselineSet] = useState(false);

  // Reset form when modal opens or service changes
  useEffect(() => {
    if (opened) {
      // Increment key to force CodeableConceptInput to remount with fresh defaultValue
      setInputKey((prev) => prev + 1);
      setValidationError(null);
      setBaselineSet(true); // Flag to capture baseline after state is set
      if (service) {
        setFormData({ ...service });
      } else {
        setFormData(createEmptyService());
      }
    } else {
      setBaselineSet(false);
    }
  }, [opened, service]);

  // Capture baseline after form state is initialized
  useEffect(() => {
    if (opened && baselineSet) {
      baselineRef.current = { ...formData };
      setBaselineSet(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  // Clear validation error on any change
  const clearValidation = useCallback(() => {
    if (validationError) {
      setValidationError(null);
    }
  }, [validationError]);

  // Change detection for FormActionButtons
  const hasUnsavedChanges = (): boolean => {
    if (!baselineRef.current) {
      return false;
    }

    try {
      return !isEqual(formData, baselineRef.current);
    } catch {
      return true;
    }
  };

  // Discard handler for FormActionButtons
  const handleDiscardChanges = () => {
    if (baselineRef.current) {
      setFormData({ ...baselineRef.current });
    }
    setInputKey((prev) => prev + 1); // Reset CodeableConceptInput
    setValidationError(null);
  };

  const handleSave = () => {
    // Validate required fields
    if (!formData.service?.coding?.[0]?.code && !formData.service?.text) {
      setValidationError(
        t('therapeuticServices.modal.validationServiceRequired', {
          defaultValue: 'Please select or enter a service',
        })
      );
      return;
    }

    // Validate startDate is required
    if (!formData.startDate) {
      setValidationError(
        t('therapeuticServices.modal.validationDateRequired', {
          defaultValue: 'Start date is required',
        })
      );
      return;
    }

    // Validate terminationDate is required when status is stopped or completed
    const requiresEndDate = ['stopped', 'completed'].includes(formData.status || '');
    if (requiresEndDate && !formData.terminationDate) {
      setValidationError(
        t('therapeuticServices.modal.validationEndDateRequired', {
          defaultValue: 'Termination date is required for this status',
        })
      );
      return;
    }

    // Validate date range if both dates are provided
    if (
      formData.terminationDate &&
      formData.startDate > formData.terminationDate
    ) {
      setValidationError(
        t('therapeuticServices.modal.validationDateRange', {
          defaultValue: 'Termination date must be after start date',
        })
      );
      return;
    }

    onSave(formData);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={() => formActionsRef.current?.handleGuardedClose()}
      closeOnClickOutside={false}
      closeOnEscape={false}
      title={
        mode === 'add'
          ? t('therapeuticServices.modal.addTitle', {
              defaultValue: 'Add Therapeutic Service',
            })
          : t('therapeuticServices.modal.editTitle', {
              defaultValue: 'Edit Therapeutic Service',
            })
      }
      size="lg"
    >
      <Stack gap="md">
        {/* Validation Error */}
        {validationError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {validationError}
          </Alert>
        )}

        {/* Service Name */}
        <CodeableConceptInput
          key={`service-${inputKey}`}
          path="Procedure.code"
          binding={THERAPEUTIC_SERVICE_VALUESET}
          label={t('therapeuticServices.serviceLabel', {
            defaultValue: 'Service',
          })}
          name="service-name"
          maxValues={1}
          defaultValue={formData.service}
          onChange={(code) => {
            setFormData((prev) => ({ ...prev, service: code }));
            clearValidation();
          }}
          outcome={undefined}
          required
        />

        {/* Status */}
        <Select
          label={t('therapeuticServices.statusLabel', {
            defaultValue: 'Status',
          })}
          data={STATUS_OPTIONS.map((status: Procedure['status']) => ({
            value: status,
            label: t(`therapeuticServices.statusOptions.${status}` as never, {
              defaultValue: status,
            }),
          }))}
          value={formData.status || null}
          onChange={(value) => {
            setFormData((prev) => ({
              ...prev,
              status: value as Procedure['status'],
              // Clear termination date when status changes to not require it
              ...(!['stopped', 'completed'].includes(value || '') && {
                terminationDate: null,
              }),
            }));
            clearValidation();
          }}
          required
        />

        {/* Dates */}
        <Group grow>
          <DateInputWrapper
            label={t('therapeuticServices.startDateLabel', {
              defaultValue: 'Start Date',
            })}
            value={formData.startDate}
            onChange={(date: Date | null) => {
              setFormData((prev) => ({ ...prev, startDate: date }));
              clearValidation();
            }}
            required
          />
          {(['stopped', 'completed'] as Procedure['status'][]).includes(
            formData.status || ('in-progress' as Procedure['status'])
          ) && (
            <DateInputWrapper
              label={t('therapeuticServices.terminationDateLabel', {
                defaultValue: 'Termination Date',
              })}
              value={formData.terminationDate}
              onChange={(date: Date | null) => {
                setFormData((prev) => ({ ...prev, terminationDate: date }));
                clearValidation();
              }}
              required
            />
          )}
        </Group>

        {/* Patient Response */}
        <Textarea
          label={t('therapeuticServices.responseLabel', {
            defaultValue: 'Patient Response',
          })}
          placeholder={t('therapeuticServices.responsePlaceholder', {
            defaultValue: "Describe patient's response to this service...",
          })}
          value={formData.response}
          onChange={(e) => {
            setFormData((prev) => ({
              ...prev,
              response: e.currentTarget.value,
            }));
            clearValidation();
          }}
          rows={3}
        />

        {/* Actions */}
        <FormActionButtons
          ref={formActionsRef}
          onSave={handleSave}
          onDiscard={handleDiscardChanges}
          onClose={onClose}
          hasUnsavedChanges={hasUnsavedChanges()}
          saveLabel={
            mode === 'add'
              ? t('therapeuticServices.modal.addButton', {
                  defaultValue: 'Add',
                })
              : t('therapeuticServices.modal.saveButton', {
                  defaultValue: 'Save',
                })
          }
        />
      </Stack>
    </Modal>
  );
};
